const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function getApiBase() {
  try {
    const backendHost = new URL(BACKEND_URL).hostname;
    if (typeof window !== 'undefined' && window.location.hostname !== backendHost) {
      return '/api';
    }
  } catch {}
  return `${BACKEND_URL}/api`;
}

const API_BASE = getApiBase();

// Default request timeout (ms). Applied uniformly to apiGet/Post/Patch/Put/Delete
// and to adminGet/Post/Patch/Put. Long-running backtests / fit-now calls may
// override by passing { timeoutMs } in the second argument.
const DEFAULT_TIMEOUT_MS = 45_000;

class ApiTimeoutError extends Error {
  constructor(url, timeoutMs) {
    super(`Request timeout (${timeoutMs}ms): ${url}`);
    this.name = 'ApiTimeoutError';
    this.isTimeout = true;
  }
}

class ApiNetworkError extends Error {
  constructor(url, cause) {
    super(`Network error: ${cause?.message || cause || 'fetch failed'}`);
    this.name = 'ApiNetworkError';
    this.isNetwork = true;
    this.cause = cause;
  }
}

/**
 * Safe fetch with AbortController timeout + postMessage-aware XHR fallback.
 * Throws `ApiTimeoutError` on timeout, `ApiNetworkError` on connectivity
 * failures. HTTP-level errors are left to `handleResponse`.
 */
async function safeFetch(url, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    return res;
  } catch (err) {
    if (err && err.name === 'AbortError') {
      throw new ApiTimeoutError(url, timeoutMs);
    }
    if (err && err.message && err.message.includes('postMessage')) {
      // Fallback to XHR when the platform strips postMessage from fetch.
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open(options.method || 'GET', url, true);
        xhr.timeout = timeoutMs;
        xhr.withCredentials = true;
        if (options.headers) {
          Object.entries(options.headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
        }
        xhr.onload = () => {
          resolve({
            ok: xhr.status >= 200 && xhr.status < 300,
            status: xhr.status,
            statusText: xhr.statusText,
            json: () => Promise.resolve(JSON.parse(xhr.responseText)),
          });
        };
        xhr.onerror = () => reject(new ApiNetworkError(url, new Error('XHR error')));
        xhr.ontimeout = () => reject(new ApiTimeoutError(url, timeoutMs));
        xhr.send(options.body || null);
      });
    }
    throw new ApiNetworkError(url, err);
  } finally {
    clearTimeout(timer);
  }
}

async function handleResponse(res) {
  if (res.status === 401 || res.status === 403) {
    // Auth failure — redirect to login
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    throw new Error('Authentication required');
  }
  if (!res.ok) throw new Error(`Errore API ${res.status}: ${res.statusText}`);
  return res.json();
}

export async function apiGet(path, opts = {}) {
  const res = await safeFetch(`${API_BASE}${path}`,
    { credentials: 'include' }, opts.timeoutMs);
  return handleResponse(res);
}

export async function apiPost(path, data, opts = {}) {
  const res = await safeFetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  }, opts.timeoutMs);
  return handleResponse(res);
}

export async function apiPatch(path, data = {}, opts = {}) {
  const res = await safeFetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  }, opts.timeoutMs);
  return handleResponse(res);
}

export async function apiPut(path, data, opts = {}) {
  const res = await safeFetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(data),
  }, opts.timeoutMs);
  return handleResponse(res);
}

export async function apiDelete(path, opts = {}) {
  const res = await safeFetch(`${API_BASE}${path}`, {
    method: 'DELETE',
    credentials: 'include',
  }, opts.timeoutMs);
  return handleResponse(res);
}

async function handleAdminResponse(res) {
  if (res.status === 401 || res.status === 403) {
    if (res.status === 401 && typeof window !== 'undefined') {
      window.location.href = '/login';
      throw new Error('Authentication required');
    }
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    // Surface backend `detail` if any so the caller can render a useful
    // message instead of a generic "Errore API 500". Falls back to the
    // raw text/status if the body is not JSON.
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.detail || JSON.stringify(body);
    } catch {
      try { detail = (await res.text()) || ''; } catch { /* ignore */ }
    }
    const e = new Error(`Errore API ${res.status}${detail ? ` — ${detail.slice(0, 300)}` : ''}`);
    e.status = res.status;
    e.detail = detail;
    throw e;
  }
  return res.json();
}

export async function adminGet(path, pin, opts = {}) {
  const res = await safeFetch(`${API_BASE}${path}`, {
    headers: { 'X-Admin-Pin': pin },
    credentials: 'include',
  }, opts.timeoutMs);
  return handleAdminResponse(res);
}

export async function adminPost(path, pin, data = {}, opts = {}) {
  const res = await safeFetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Pin': pin },
    credentials: 'include',
    body: JSON.stringify(data),
  }, opts.timeoutMs);
  return handleAdminResponse(res);
}

export async function adminPut(path, pin, data = {}, opts = {}) {
  const res = await safeFetch(`${API_BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Pin': pin },
    credentials: 'include',
    body: JSON.stringify(data),
  }, opts.timeoutMs);
  return handleAdminResponse(res);
}

export async function adminPatch(path, pin, data = {}, opts = {}) {
  const res = await safeFetch(`${API_BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', 'X-Admin-Pin': pin },
    credentials: 'include',
    body: JSON.stringify(data),
  }, opts.timeoutMs);
  return handleAdminResponse(res);
}

export async function apiUpload(path, file) {
  const formData = new FormData();
  formData.append('file', file);
  // Uploads get a longer timeout since files may be large.
  const res = await safeFetch(`${API_BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  }, 120_000);
  if (res.status === 401 || res.status === 403) {
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    throw new Error('Authentication required');
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Errore API ${res.status}`);
  }
  return res.json();
}

export { ApiTimeoutError, ApiNetworkError };

